<template>
  <div class="my-esim">
    <div class="topbar">
      <div class="title">我的eSIM</div>
    </div>

    <div class="list" v-if="loaded && esims.length === 0">
      <div class="empty">暂无 eSIM 数据</div>
    </div>

    <div class="list" v-else>
      <div v-for="item in esims" :key="item.iccid" class="card">
        <div class="row">
          <div class="label">套餐</div>
          <div class="value">{{ item.package_title || item.package_id }}</div>
        </div>
        <div class="row">
          <div class="label">ICCID</div>
          <div class="value" style="display:flex;align-items:center;gap:6px;">
            <span>{{ item.iccid }}</span>
            <el-button v-if="item.iccid" size="mini" type="text" @click="copyToClipboard(item.iccid, 'ICCID')">复制</el-button>
          </div>
        </div>
        <div class="row">
          <div class="label">有效期(天)</div>
          <div class="value">{{ item.validity_days != null ? item.validity_days : '-' }}</div>
        </div>
        <div class="row">
          <div class="label">总流量</div>
          <div class="value">{{ item.data_text || '-' }}</div>
        </div>
        <div class="row">
          <div class="label">区域</div>
          <div class="value">{{ item.category_path || '-' }}</div>
        </div>
        <div class="row">
          <div class="label">商品ID</div>
          <div class="value">{{ item.package_id || '-' }}</div>
        </div>
        <div class="row">
          <div class="label">订单号</div>
          <div class="value" style="display:flex;align-items:center;gap:6px;">
            <span>{{ item.order_sn || '-' }}</span>
            <el-button v-if="item.order_sn" size="mini" type="text" @click="copyOrderSn(item.order_sn)">复制</el-button>
          </div>
        </div>
        <div class="row ops">
          <el-button size="mini" type="success" @click="viewQrcode(item)">激活二维码</el-button>
          <el-button size="mini" type="primary" @click="viewUsage(item)">用量查询</el-button>
        </div>
      </div>
    </div>

    <el-dialog title="eSIM 用量" :visible.sync="usageVisible" width="300px" center>
      <div v-if="usage">
        <div class="usage-row"><span>状态</span><b>{{ usage.status }}</b></div>
        <div class="usage-row"><span>剩余流量(MB)</span><b>{{ usage.remaining }}</b></div>
        <div class="usage-row"><span>总流量(MB)</span><b>{{ usage.total }}</b></div>
        <div class="usage-row"><span>到期时间</span><b>{{ usage.expired_at }}</b></div>
      </div>
      <div v-else class="usage-row">加载中...</div>
      <span slot="footer" class="dialog-footer">
        <el-button type="primary" @click="usageVisible = false">关闭</el-button>
      </span>
    </el-dialog>

    <el-dialog title="激活二维码" :visible.sync="qrcodeVisible" width="320px" center>
      <div v-if="currentQrcodeUrl" class="qrcode-container">
        <img :src="currentQrcodeUrl" alt="激活二维码" class="qrcode-img" />
      </div>
      <div v-else class="qrcode-container">
        <p style="text-align: center; color: #999;">暂无二维码</p>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button type="primary" @click="qrcodeVisible = false">关闭</el-button>
      </span>
    </el-dialog>
    <navBar :idx="3" />
  </div>
</template>

<script>
import navBar from '@/components/navBar'

export default {
  name: 'myEsim',
  components: { navBar },
  /**
   * 组件本地状态
   * @returns {Object} 响应式数据对象
   * @property {Array<Object>} esims eSIM 列表
   * @property {boolean} loaded 列表是否已加载完成
   * @property {boolean} usageVisible 用量弹窗是否可见
   * @property {Object|null} usage 当前查询到的用量信息
   * @property {string} currentIccid 当前查看用量的 ICCID
   */
  data() {
    return {
      esims: [],
      loaded: false,
      usageVisible: false,
      usage: null,
      currentIccid: '',
      qrcodeVisible: false,
      currentQrcodeUrl: ''
    }
  },
  /**
   * 生命周期：组件创建完成时拉取 eSIM 列表
   */
  created() {
    this.fetchMyEsims()
  },
  methods: {
    /**
     * 获取当前用户的 eSIM 列表
     * @returns {Promise<void>} 无显式返回；成功后更新 esims，并设置 loaded=true
     */
    async fetchMyEsims() {
      try {
        const { data } = await this.$api.request('Member/myEsims', {})
        this.esims = Array.isArray(data) ? data : []
      } catch (error) {
        this.$message({ message: '获取我的eSIM失败，请检查网络！', type: 'warning' })
      } finally {
        this.loaded = true
      }
    },
    /**
     * 打开用量弹窗并查询指定 eSIM 的用量信息
     * @param {Object} item eSIM 列表项
     * @param {string|number} item.iccid eSIM 的 ICCID
     * @returns {Promise<void>} 无显式返回；成功后更新 usage
     */
    async viewUsage(item) {
      try {
        this.usageVisible = true
        this.usage = null
        this.currentIccid = String(item.iccid)
        const { data } = await this.$api.request('Member/esimUsage', { iccid: this.currentIccid })
        this.usage = data
      } catch (error) {
        this.$message({ message: '查询用量失败或暂不支持该商品', type: 'warning' })
      }
    },
    /**
     * 复制指定文本到系统剪贴板
     * @param {string|number} text 待复制的文本
     * @param {string} subject 文案主题（用于提示），例如：'订单号'、'ICCID'
     * @returns {Promise<void>} 无显式返回；复制成功后展示成功提示
     */
    async copyToClipboard(text, subject) {
      try {
        const value = String(text || '')
        if (!value) {
          this.$message({ message: `无可复制的${subject}` , type: 'warning' })
          return
        }
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(value)
        } else {
          const textarea = document.createElement('textarea')
          textarea.value = value
          textarea.style.position = 'fixed'
          textarea.style.left = '-9999px'
          document.body.appendChild(textarea)
          textarea.focus()
          textarea.select()
          document.execCommand('copy')
          document.body.removeChild(textarea)
        }
        this.$message({ message: `${subject}已复制` , type: 'success' })
      } catch (error) {
        this.$message({ message: '复制失败，请手动选择复制', type: 'warning' })
      }
    },
    /**
     * 复制订单号到系统剪贴板
     * @param {string} orderSn 订单号
     * @returns {Promise<void>} 无显式返回；复制成功后提示
     */
    async copyOrderSn(orderSn) {
      await this.copyToClipboard(orderSn, '订单号')
    },
    /**
     * 打开二维码弹窗并显示激活二维码
     * @param {Object} item eSIM 列表项
     * @param {string} item.qrcode_url 二维码图片URL
     * @returns {void}
     */
    viewQrcode(item) {
      this.currentQrcodeUrl = item.qrcode_url || ''
      this.qrcodeVisible = true
    }
  }
}
</script>

<style scoped>
.my-esim { padding: 12px; }
.topbar { text-align: center; padding: 10px 0; }
.title { font-size: 18px; font-weight: 600; }
.list { margin-top: 10px; }
.empty { text-align: center; color: #999; padding: 50px 0; }
.card { background: #fff; border: 1px solid #eee; border-radius: 8px; padding: 12px; margin-bottom: 10px; }
.row { display: flex; justify-content: space-between; align-items: center; margin: 6px 0; }
.label { color: #666; font-size: 13px; }
.value { color: #333; font-size: 14px; word-break: break-all; }
.ops { justify-content: flex-end; }
.usage-row { display: flex; justify-content: space-between; margin: 6px 0; }
.usage-row span { color: #666; }
.usage-row b { color: #333; }
.qrcode-container { text-align: center; padding: 10px; }
.qrcode-img { max-width: 100%; height: auto; }
</style>





// WEBPACK FOOTER //
// src/page/myEsim.vue